import { PdfGen, PdfSize, PdfUnit, PdfRect, PdfPoint, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { IItemCount } from '@educraft/base';
import { OptsImageSingle } from '../optsImageSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';

/**
 * Priklady — landscape matching game.
 *
 * Two halves side by side (cut along the middle vertical line):
 *   Left half:  2×5 grid — question text + answer image at bottom-right corner
 *   Right half: 2×5 grid — images shuffled randomly + " =" label
 *
 * Student draws lines to match each equation on the left to its image on the right.
 *
 * Port of: EduFrame.ImgSingle.Generators.Priklady
 */
export class Prirazeni implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 6;
  }
  countMax(): number {
    return 16;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const page = pdf.addPage(true); // landscape A4

    const {A4H} = PdfSize;
    const margin = PdfUnit.FromMillimeters(5);
    const textOffset = PdfUnit.FromMillimeters(3);
    const imageSize = PdfUnit.FromMillimeters(20);

    const halfW = A4H.width / 2 - 2 * margin;
    const halfH = A4H.height - 2 * margin;

    const half1 = new PdfRect(margin, margin, halfW, halfH);
    const half2 = new PdfRect(A4H.width / 2 + margin, margin, halfW, halfH);

    page.drawRectangle(GUtil.basePen, null, half1);
    page.drawRectangle(GUtil.basePen, null, half2);
    page.cutCross(new PdfPoint(A4H.width / 2, 0), false, true); // vertical cut line

    const count = opts.list.length;
    const array = genSingle(opts.data, opts.generator, count, true, true);
    const mp = GUtil.genMap(opts.data.random, count);

    const cols = 2;
    const rows = Math.ceil(count / cols);
    const szW = half1.width / cols;
    const szH = half1.height / rows;

    for (let x = 0; x < cols; x++) {
      for (let y = 0; y < rows; y++) {
        const index = x * rows + y;
        if (index >= count) continue;

        const shuffledIndex = mp[index];
        const x1 = Math.floor(shuffledIndex / rows);
        const y1 = shuffledIndex % rows;

        const rect1 = new PdfRect(half1.x + szW * x, half1.y + szH * y, szW, szH);
        const rect2 = new PdfRect(half2.x + szW * x1, half2.y + szH * y1, szW, szH);

        const r1 = new PdfRect(
          rect1.x + textOffset,
          rect1.y,
          rect1.width - textOffset,
          rect1.height
        );
        const r2 = new PdfRect(
          rect2.x + textOffset,
          rect2.y,
          rect2.width - textOffset,
          rect2.height
        );

        page.drawRectangle(GUtil.basePen, null, rect1);
        page.drawRectangle(GUtil.basePen, null, rect2);

        const text = array[index].texts.join(' ');
        const f = page.adjustFont(rect1.width, [text], GUtil.baseFont);
        page.drawString(text, f, PdfBrush.Black, null, r1, ETextAlign.TopLeft);

        const imgItem = opts.list.length > 0 ? opts.list[index % opts.list.length] : null;
        if (imgItem?.image) {
          const rr1 = new PdfRect(
            r1.right - imageSize,
            r1.bottom - imageSize,
            imageSize,
            imageSize
          );
          const rr2 = new PdfRect(r2.x, r2.centerY - imageSize / 2, imageSize, imageSize);
          page.drawImageCenter(imgItem.image, rr1);
          page.drawImageCenter(imgItem.image, rr2);

          const eqRect = new PdfRect(rr2.right, rect2.y, rect2.right - rr2.right, rect2.height);
          page.drawString(
            ' =',
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            eqRect,
            ETextAlign.CenterLeft
          );
        }
      }
    }
  }
}
